import { Stack } from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  ADD_NEW_BUSINESS_FORM_SCHEMA,
  createAddNewBusinessCoverageSchema,
  type GroupDetailsFormValues,
  type CoverageDetailsFormValues,
} from "../../../constants";

import { GroupDetails } from "./GroupDetails";
import { CoverageDetails } from "./CoverageDetails";

export type AddNewBusinessFormHandle = {
  getFormValues: () => {
    step1: GroupDetailsFormValues;
    step2: CoverageDetailsFormValues;
  };
  resetStep2: (values?: CoverageDetailsFormValues) => void;
  step1FormIsDirty: boolean;
  step2FormIsDirty: boolean;
};

type AddNewBusinessFormProps = {
  step: number;
  setConfirmButtonDisabled: (value: boolean) => void;
};

export const AddNewBusinessForm = forwardRef<
  AddNewBusinessFormHandle,
  AddNewBusinessFormProps
>(({ step, setConfirmButtonDisabled }, ref) => {
  const [lockForm, setLockForm] = useState<boolean>(false);
  const step1Methods = useForm<GroupDetailsFormValues>({
    resolver: zodResolver(ADD_NEW_BUSINESS_FORM_SCHEMA),
    mode: "all",
    defaultValues: {
      businessType: "newSale",
      groupType: "newGroup",
      groupName: "",
      groupId: null,
    },
  });

  const businessType = step1Methods.watch("businessType");
  const coverageSchema = useMemo(
    () => createAddNewBusinessCoverageSchema(businessType),
    [businessType],
  );

  const step2Methods = useForm<CoverageDetailsFormValues>({
    resolver: zodResolver(coverageSchema),
    mode: "all",
    defaultValues: {
      carrier: null,
      originalSaleDate: null,
      premiumEligibleDate: null,
      policyType: null,
      planType: null,
      averageMonthlyPremium: "",
      fundingType: null,
      participants: null,
      brokerSplit: null,
    },
  });

  useImperativeHandle(ref, () => ({
    getFormValues: () => ({
      step1: step1Methods.getValues(),
      step2: step2Methods.getValues(),
    }),
    resetStep2: (values?: Partial<CoverageDetailsFormValues>) => {
      const currentValues = step2Methods.getValues();

      step2Methods.reset({
        carrier: values?.carrier ?? currentValues.carrier,
        originalSaleDate:
          values?.originalSaleDate ?? currentValues.originalSaleDate,
        premiumEligibleDate:
          values?.premiumEligibleDate ?? currentValues.premiumEligibleDate,
        fundingType: values?.fundingType ?? currentValues.fundingType,
        participants: values?.participants ?? currentValues.participants,
        brokerSplit: values?.brokerSplit ?? currentValues.brokerSplit,
        policyType: null,
        planType: null,
        averageMonthlyPremium: "",
      });
      setLockForm(true);
    },
    step1FormIsDirty: step1Methods.formState.isDirty,
    step2FormIsDirty: step2Methods.formState.isDirty,
  }));

  const {
    formState: { isValid: step1FormIsValid },
  } = step1Methods;
  const {
    formState: { isValid: step2FormIsValid },
  } = step2Methods;

  useEffect(() => {
    const isConfirmButtonDisabled =
      step === 1 ? !step1FormIsValid : !step2FormIsValid;

    setConfirmButtonDisabled(isConfirmButtonDisabled);
  }, [step, setConfirmButtonDisabled, step1FormIsValid, step2FormIsValid]);

  const groupId = step1Methods.watch("groupId");
  const brokerName =
    groupId?.brokerName ?? step1Methods.watch("groupName") ?? "";
  const brokerSk = groupId?.brokerSk ?? "System Generating";

  return (
    <Stack spacing={3} direction="column">
      {step === 1 && (
        <FormProvider {...step1Methods}>
          <GroupDetails />
        </FormProvider>
      )}
      {step === 2 && (
        <FormProvider {...step2Methods}>
          <CoverageDetails
            brokerName={brokerName}
            brokerSk={brokerSk}
            lockForm={lockForm}
            businessType={businessType}
          />
        </FormProvider>
      )}
    </Stack>
  );
});

AddNewBusinessForm.displayName = "AddNewBusinessForm";
